import { AllocationWithAmount } from '../payment-method-selection-dialog';

export type CheckAllocationSplit = {
  allocationId: number;
  amount: number;
};

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

/**
 * Bucket-fill allocations for a single check amount.
 *
 * Fills allocations in the given order: first allocation first, then next, etc.
 * `alreadyAllocatedByAllocationId` is subtracted from each allocation's target amount.
 */
export function bucketFillCheckAllocations(params: {
  allocationTotals: AllocationWithAmount[];
  checkAmount: number;
  alreadyAllocatedByAllocationId?: Record<number, number>;
}): CheckAllocationSplit[] {
  const { allocationTotals, checkAmount, alreadyAllocatedByAllocationId } =
    params;
  const already = alreadyAllocatedByAllocationId ?? {};

  let remaining = round2(checkAmount);
  if (!Number.isFinite(remaining) || remaining <= 0) return [];

  const splits: CheckAllocationSplit[] = [];

  for (const alloc of allocationTotals) {
    if (remaining <= 0) break;

    const used = round2(already[alloc.allocationId] ?? 0);
    const available = round2((alloc.amount || 0) - used);
    if (available <= 0) continue;

    const take = round2(Math.min(available, remaining));
    if (take <= 0) continue;

    splits.push({ allocationId: alloc.allocationId, amount: take });
    remaining = round2(remaining - take);
  }

  return splits;
}
